import { supabase } from '../lib/supabase';
import { withTimeout } from '../utils/supabaseTimeout';

const isSchemaError = (error) => {
  if (!error) return false;

  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') return true;
    if (errorClass === '23') return false;
  }

  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }

  return false;
};

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

export const emailScheduleService = {
  async getAllSchedules() {
    try {
      const { data, error } = await supabase
        ?.from('email_schedules')
        ?.select(`
          *,
          creator:user_profiles!email_schedules_created_by_fkey(
            id,
            full_name,
            email
          ),
          recipients:email_recipients(
            id,
            recipient_name,
            recipient_email,
            is_active
          )
        `)
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching email schedules:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch email schedules:', error);
      throw error;
    }
  },

  async getScheduleById(scheduleId) {
    try {
      const { data, error } = await supabase
        ?.from('email_schedules')
        ?.select(`
          *,
          creator:user_profiles!email_schedules_created_by_fkey(
            id,
            full_name,
            email
          ),
          recipients:email_recipients(
            id,
            recipient_name,
            recipient_email,
            is_active
          )
        `)
        ?.eq('id', scheduleId)
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error fetching schedule:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to fetch schedule:', error);
      throw error;
    }
  },

  async createSchedule(scheduleData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('User not authenticated');

      const snakeData = toSnakeCase({
        ...scheduleData,
        createdBy: user?.id
      });

      const { data, error } = await withTimeout(
        supabase
          ?.from('email_schedules')
          ?.insert(snakeData)
          ?.select()
          ?.single(),
        'createSchedule',
        15000
      );

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error creating schedule:', error?.message);
        throw new Error(error?.message || 'Error al crear el programa');
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to create schedule:', error);
      throw error;
    }
  },

  async updateSchedule(scheduleId, scheduleData) {
    try {
      const snakeData = toSnakeCase(scheduleData);

      const { data, error } = await withTimeout(
        supabase
          ?.from('email_schedules')
          ?.update(snakeData)
          ?.eq('id', scheduleId)
          ?.select()
          ?.single(),
        'updateSchedule',
        15000
      );

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error updating schedule:', error?.message);
        throw new Error(error?.message || 'Error al actualizar el programa');
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to update schedule:', error);
      throw error;
    }
  },

  async deleteSchedule(scheduleId) {
    try {
      const { error } = await supabase
        ?.from('email_schedules')
        ?.delete()
        ?.eq('id', scheduleId);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error deleting schedule:', error?.message);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Failed to delete schedule:', error);
      throw error;
    }
  },

  async addRecipient(scheduleId, recipientData) {
    try {
      const snakeData = toSnakeCase({
        scheduleId,
        ...recipientData
      });

      const { data, error } = await supabase
        ?.from('email_recipients')
        ?.insert(snakeData)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error adding recipient:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to add recipient:', error);
      throw error;
    }
  },

  async updateRecipient(recipientId, recipientData) {
    try {
      const snakeData = toSnakeCase(recipientData);

      const { data, error } = await supabase
        ?.from('email_recipients')
        ?.update(snakeData)
        ?.eq('id', recipientId)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error updating recipient:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to update recipient:', error);
      throw error;
    }
  },

  async deleteRecipient(recipientId) {
    try {
      const { error } = await supabase
        ?.from('email_recipients')
        ?.delete()
        ?.eq('id', recipientId);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error deleting recipient:', error?.message);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Failed to delete recipient:', error);
      throw error;
    }
  },

  async sendTestEmail(scheduleId) {
    try {
      const schedule = await this.getScheduleById(scheduleId);
      if (!schedule) throw new Error('Schedule not found');

      const recipients = schedule?.recipients
        ?.filter(r => r?.isActive)
        ?.map(r => r?.recipientEmail) || [];

      if (recipients?.length === 0) {
        throw new Error('No active recipients found');
      }

      let reportData = {};
      
      if (schedule?.reportType === 'financial_report') {
        reportData = {
          totalRevenue: 125000,
          partsCost: 45000,
          laborCost: 35000,
          profitMargin: 36.0
        };
      } else if (schedule?.reportType === 'client_analytics') {
        reportData = {
          topClients: [
            { name: 'Cliente A', revenue: 50000, serviceCount: 12 },
            { name: 'Cliente B', revenue: 35000, serviceCount: 8 },
            { name: 'Cliente C', revenue: 28000, serviceCount: 6 }
          ],
          repeatServiceRate: 68.5
        };
      } else if (schedule?.reportType === 'threshold_alerts') {
        reportData = {
          alerts: [
            { type: 'Revenue Target', message: 'Daily revenue below target', priority: 'warning' },
            { type: 'Overdue Invoices', message: '5 invoices overdue', priority: 'critical' }
          ]
        };
      }

      const { data, error } = await supabase?.functions?.invoke('send-scheduled-reports', {
        body: {
          scheduleId: schedule?.id,
          reportType: schedule?.reportType,
          recipients,
          reportData
        }
      });

      if (error) {
        console.error('Error sending test email:', error?.message);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Failed to send test email:', error);
      throw error;
    }
  },

  async getEmailLogs(scheduleId = null, limit = 50) {
    try {
      let query = supabase
        ?.from('email_logs')
        ?.select('*')
        ?.order('sent_at', { ascending: false })
        ?.limit(limit);

      if (scheduleId) {
        query = query?.eq('schedule_id', scheduleId);
      }

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching email logs:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch email logs:', error);
      throw error;
    }
  }
};